import { ComputingOption } from "@/service/models/request/computation/computing-option";
import { ResponseModelInterfaceStatic } from "@/service/models/response-model-interface";
import { RepositoryCurve } from "@/utilities/graphing/data-repository/curve";
import { DataRepository } from "@/utilities/graphing/data-repository/repository";
import { staticImplements } from "@/utilities/static-implements";
import { DatesRange } from "@/utilities/types/dates-range";
import { XmlElementWrapper } from "@/utilities/xml-helpers/xml-element-wrapper";
import { CycleData } from "./cycle-data/cycle-data";
import { CycleParameter } from "./cycle-data/cycle-parameter";
import { DataResponse } from "./data-response";

@staticImplements<ResponseModelInterfaceStatic>()
export class DataPrediction implements DataResponse
{
    public constructor(
        public analyteIds: string[],
        public cycleData: CycleData[],
    )
    {
    }

    /**
     * @inheritdoc
     */
    public static makeFromXml(elem: XmlElementWrapper): DataPrediction
    {
        return new this(
            elem.translatedElemValues("analyteId"),
            elem.firstChildByTag("cycleDatas").mapForEachChildTag("cycleData", (subElem) => CycleData.makeFromXml(subElem))
        );
    }

    /**
     * @inheritdoc
     */
    public fillDataRepository(
        dataRepository: DataRepository,
        computingParamType: ComputingOption.ParametersType
    ): void
    {
        const dataset = this.getRepositoryDataset(dataRepository, computingParamType);

        if (dataset !== null) {
            this.insertCyclePointsInCurve(dataset);
        }
    }

    /**
     * Get the parameters of the 1st cycle in the prediction response.
     * If there are no cycles or if parameters where not requested in the query, an empty
     * array is returned.
     */
    public getFirstCycleParameters(): CycleParameter[]
    {
        return this.cycleData[0].parameters ?? [];
    }

    /**
     * @inheritdoc
     */
    fillGraphDataAdjustmentsRepository(dataRepository: DataRepository): void
    {
        this.insertCyclePointsInCurve(dataRepository.selectedAdjustmentPredictionData);
    }

    protected getRepositoryDataset(
        dataRepository: DataRepository,
        computingParamType: ComputingOption.ParametersType
    ): RepositoryCurve|null
    {
        switch (computingParamType) {
            case ComputingOption.ParametersType.APRIORI:
                return dataRepository.aprioriPredictionData;

            case ComputingOption.ParametersType.APOSTERIORI:
                return dataRepository.aposterioriPredictionData;

            case ComputingOption.ParametersType.POPULATION:
                return dataRepository.populationPredictionData;

            default:
                return null;
        }
    }

    protected insertCyclePointsInCurve(curve: RepositoryCurve): void
    {
        for (const cycle of this.cycleData) {
            const cycleStats = cycle.statistics?.toGraphPredictionStatistics(cycle.start, cycle.end) ?? null;

            for (const [time, value] of cycle.getValuesByDate()) {
                curve.insertDataPoint(time, value, cycleStats);
            }

            curve.available.add(new DatesRange(cycle.start, cycle.end));
        }
    }
}
